import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import { type MouseEvent, useState } from 'react'
import { Button, IconButton, Typography } from 'shared/ui'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { type ModalType } from 'shared/ui/Modal/store/types'
import { getAriaLabel } from 'shared/lib'
import { DefaultModalActions } from 'shared/ui/Modal/ui/DefaultModal/DefaultModalActions'
import styles from './styles.module.scss'

export const DefaultModal = observer(({ modal, hide }: { modal: ModalType; hide: boolean }) => {
  const {
    position,
    width = 480,
    id,
    title,
    titleDividerContent,
    subtitle,
    primaryAction,
    ModalError,
    ModalAlert,
    ModalBottomError,
    ModalActions,
    ModalContent,
    ModalContentProps,
    showCloseButton = true,
    closeText = 'Cancel',
    showCloseIcon = true,
    showHeader = true,
    pureContent,
    paddingTopModal,
    paddingContent,
    fullHeight,
    hiddenContent,
    secondaryAction,
    className,
    customTitle,
    autoOverflow,
  } = modal

  const [loadingPrimary, setLoadingPrimary] = useState(false)

  const onClose = () => {
    modalStore.closeModal(id)
  }

  const showActionsBlock = showCloseButton || primaryAction || ModalActions
  const ariaLabel = 'DefaultModal'

  const handlePrimary = async (e: MouseEvent<HTMLButtonElement>) => {
    if (primaryAction?.onAction) {
      try {
        setLoadingPrimary(true)
        await primaryAction?.onAction(e)
      } finally {
        setLoadingPrimary(false)
      }
    }
  }

  return (
    <div
      className={classNames(
        styles.wrap,
        position && styles[position],
        fullHeight && styles.fullHeight,
        {
          [styles.hiddenContent]: hiddenContent,
          [styles.autoOverflow]: autoOverflow,
        },
        className
      )}
      style={{
        ...(paddingTopModal ? { padding: paddingTopModal } : null),
      }}
      aria-label={getAriaLabel(ariaLabel, 'content')}
    >
      <div
        id={`modal-card-${id}`}
        className={classNames(styles.card)}
        style={{
          width,
          visibility: hide ? 'hidden' : 'unset',
        }}
        aria-label={getAriaLabel(ariaLabel, 'card')}
      >
        {showHeader && (
          <div className={styles.header} aria-label={getAriaLabel(ariaLabel, 'header')}>
            <div className={styles.titleWrapper}>
              {customTitle ||
                (title && (
                  <div
                    className={styles.title}
                    aria-label={getAriaLabel(ariaLabel, 'header_title')}
                  >
                    <Typography variant={'body-xs-regular'} ariaLabel={'title'}>
                      {title}
                    </Typography>
                    {titleDividerContent && (
                      <>
                        <span className={styles.divider} />
                        <Typography variant={'body-xs-regular'} ariaLabel={'title'}>
                          {titleDividerContent}
                        </Typography>
                      </>
                    )}
                  </div>
                ))}
              {subtitle && (
                <Typography
                  className={styles.subtitle}
                  variant={'body-xs-regular'}
                  ariaLabel={'subtitle'}
                  color='tertiary'
                >
                  {subtitle}
                </Typography>
              )}
            </div>
            {showCloseIcon && (
              <IconButton
                icon={'close'}
                size={'small'}
                color={'secondary'}
                variant={'icon'}
                onClick={onClose}
                ariaLabel='DefaultModal_close'
              />
            )}
          </div>
        )}
        {ModalAlert && (
          <div
            className={styles.alertContainer}
            aria-label={getAriaLabel(ariaLabel, 'alertContainer')}
          >
            <ModalAlert {...ModalContentProps} />
          </div>
        )}

        {ModalError && (
          <div
            className={styles.errorContainer}
            aria-label={getAriaLabel(ariaLabel, 'errorContainer')}
          >
            <ModalError {...ModalContentProps} />
          </div>
        )}

        {pureContent ? (
          <>
            {ModalContent && <ModalContent {...ModalContentProps} />}
            {ModalBottomError && (
              <div
                className={styles.errorContainer}
                aria-label={getAriaLabel(ariaLabel, 'errorContainer')}
              >
                <ModalBottomError {...ModalContentProps} />
              </div>
            )}
          </>
        ) : (
          <>
            <div
              className={classNames(styles.content, {
                [styles.hiddenContent]: hiddenContent,
              })}
              style={{
                ...(paddingContent ? { padding: paddingContent } : null),
              }}
              aria-label={getAriaLabel(ariaLabel, 'content')}
            >
              {ModalContent && <ModalContent {...ModalContentProps} />}
            </div>
            {ModalBottomError && (
              <div
                className={styles.errorContainer}
                aria-label={getAriaLabel(ariaLabel, 'errorContainer')}
              >
                <ModalBottomError {...ModalContentProps} />
              </div>
            )}
            {showActionsBlock && (
              <DefaultModalActions ariaLabel={ariaLabel}>
                {showCloseButton && (
                  <Button
                    typeBtn={'contained'}
                    text={secondaryAction?.text || closeText}
                    size={'medium'}
                    contained={'secondary'}
                    onClick={onClose}
                  />
                )}
                {primaryAction && (
                  <Button
                    typeBtn={'contained'}
                    text={primaryAction.text}
                    disabled={Boolean(primaryAction.disabled)}
                    fullWidth={primaryAction.fullWidth}
                    onClick={handlePrimary}
                    loading={loadingPrimary}
                  />
                )}
                {ModalActions && <ModalActions {...ModalContentProps} />}
              </DefaultModalActions>
            )}
          </>
        )}
      </div>
    </div>
  )
})
